import WaterfallChart from '@/components/WaterfallChart';
import { useTheme, Text, Grid } from '@simplywallst/ui-core';
import { toCurrencyTruncated } from '@simplywallst/formatting';
import { EntityProps } from '@/components/WaterfallChart/WaterfallChart';
import type { TimeSeriesRawData } from '@/pages/CompanyReport/components/Past/hooks/useCompanyTimeSeries/queryFn';
import { getFreeCashFlowDifference, isSufficientData } from './utils';
import type { QueryStatus } from '@tanstack/react-query';
type Props = {
  rawData?: TimeSeriesRawData;
  status: QueryStatus;
};

export const FreeCashFlowChart = ({ rawData, status }: Props) => {
  const theme = useTheme();
  const charts = ['Free Cash Flow'];
  const keys = [
    'Earnings',
    'Depreciation and Amortization',
    'Stock Based Compensation',
    'Net Working Capital',
    'Others',
    'Free Cash Flow',
  ];

  if (rawData === undefined) {
    return null;
  }

  if (!isSufficientData(rawData) || status === 'error') {
    return (
      <Grid placeItems="center" $height="100%">
        <Text typography="smallStrong">
          Insufficient data to display Free Cash Flow vs Earnings Analysis
        </Text>
      </Grid>
    );
  }

  const { timeSeries, data } = rawData;
  const { reporting_unit, reporting_currency_iso } = data?.currencyInfo || {};
  const reportingUnit =
    typeof reporting_unit === 'number' ? reporting_unit : 1_000_000;
  const currencyISO =
    reporting_currency_iso === null ? 'USD' : reporting_currency_iso;

  const {
    netIncome,
    depreciationAmortizationTotal,
    stockBasedCompensation,
    changeNetWorkingCapital,
    leveredFreeCashFlow,
  } = timeSeries[timeSeries.length - 1];

  const earnings = netIncome ? netIncome * reportingUnit : 0;
  const depAndAmort = depreciationAmortizationTotal
    ? depreciationAmortizationTotal * reportingUnit
    : 0;
  const stockBasedComp = stockBasedCompensation
    ? stockBasedCompensation * reportingUnit
    : 0;
  const netWorkingCapital = changeNetWorkingCapital
    ? changeNetWorkingCapital * reportingUnit * -1 //sign should be inverted according to CIQ formula
    : 0;
  const freeCashFlow = leveredFreeCashFlow
    ? leveredFreeCashFlow * reportingUnit
    : 0;
  const totalVariation =
    earnings + depAndAmort + stockBasedComp + netWorkingCapital;

  const others =
    getFreeCashFlowDifference(
      earnings,
      depAndAmort,
      stockBasedComp,
      netWorkingCapital,
      freeCashFlow
    ) || 0;

  const entities = {
    [charts[0]]: {
      [keys[0]]: earnings,
      [keys[1]]: depAndAmort,
      [keys[2]]: stockBasedComp,
      [keys[3]]: netWorkingCapital,
      [keys[4]]: others,
      [keys[5]]: freeCashFlow,
    },
  };
  const entitiesFormatted = {
    [charts[0]]: {
      [keys[0]]: toCurrencyTruncated(earnings, 2, currencyISO),
      [keys[1]]: toCurrencyTruncated(depAndAmort, 2, currencyISO),
      [keys[2]]: toCurrencyTruncated(stockBasedComp, 2, currencyISO),
      [keys[3]]: toCurrencyTruncated(netWorkingCapital, 2, currencyISO),
      [keys[4]]: toCurrencyTruncated(others, 2, currencyISO),
      [keys[5]]: toCurrencyTruncated(freeCashFlow, 2, currencyISO),
    },
  };
  const entitiesTooltips = {
    [charts[0]]: {
      [keys[0]]: 'Earnings',
      [keys[1]]: 'Depreciation and Amortization',
      [keys[2]]: 'Stock Based Compensation',
      [keys[3]]: 'Net Working Capital',
      [keys[4]]: 'Others',
      [keys[5]]: 'Free Cash Flow',
    },
  };
  const entitiesProps: Record<string, Record<string, EntityProps>> = {
    [charts[0]]: {
      [keys[0]]: { negativeValueColor: theme.color.chart02 }, // earnings
      [keys[1]]: { renderHeight: earnings, renderDirection: 'up' }, // depAndAmort
      [keys[2]]: {
        renderHeight: earnings + depAndAmort,
        renderDirection: 'up',
      }, //stockBasedComp
      [keys[3]]: {
        renderHeight: earnings + depAndAmort + stockBasedComp,
        renderDirection: netWorkingCapital > 0 ? 'up' : 'down',
      }, // netWorkingCapital
      [keys[4]]: {
        renderHeight:
          totalVariation >= freeCashFlow
            ? earnings + depAndAmort + stockBasedComp + netWorkingCapital
            : freeCashFlow,
        renderDirection: 'down',
      }, // Other
      [keys[5]]: { negativeValueColor: theme.color.chart03 }, // Free Cash Flow
    },
  };

  const colors = [
    theme.color.chart02,
    theme.color.good,
    theme.color.good,
    theme.color.good,
    theme.color.good,
    theme.color.chart03,
  ];
  const titleColors = [
    theme.color.white,
    theme.color.white,
    theme.color.white,
    theme.color.white,
    theme.color.white,
    theme.color.white,
  ];

  return (
    <WaterfallChart
      hideTitle
      canStack={false}
      charts={charts}
      colors={colors}
      entities={entities}
      entitiesFormatted={entitiesFormatted}
      entitiesTooltips={entitiesTooltips}
      typography="tinyStrong"
      height={300}
      keys={keys}
      titleColors={titleColors}
      entitiesProps={entitiesProps}
      allowScrolling
    />
  );
};
